import React, { useState } from 'react';
import './MonthlyCalendar.css';
import { PageHeader } from '../../components/PageHeader/PageHeader';
import { BottomSheet } from '../../components/BottomSheet/BottomSheet';
import type { CalendarHistory, DaySchedule } from '../../types';

interface MonthlyCalendarProps {
  history: CalendarHistory;
  schedule: DaySchedule[];
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getStartDayOffset(year: number, month: number): number {
  const d = new Date(year, month, 1).getDay();
  return d === 0 ? 6 : d - 1; // Monday = 0
}

function toDateStr(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function getDayIndexFromDate(dateStr: string): number {
  const d = new Date(dateStr + 'T12:00:00');
  const jsDay = d.getDay();
  if (jsDay === 0) return -1; // Sunday
  return jsDay - 1;
}

export const MonthlyCalendar: React.FC<MonthlyCalendarProps> = ({ history, schedule }) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const todayStr = toDateStr(now.getFullYear(), now.getMonth(), now.getDate());
  const daysInMonth = getDaysInMonth(year, month);
  const startOffset = getStartDayOffset(year, month);

  const goPrev = () => {
    if (month === 0) { setMonth(11); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const goNext = () => {
    if (month === 11) { setMonth(0); setYear(year + 1); }
    else setMonth(month + 1);
  };

  // Month stats
  let completedDays = 0;
  let totalScheduledDays = 0;
  for (let d = 1; d <= daysInMonth; d++) {
    const ds = toDateStr(year, month, d);
    const dayIdx = getDayIndexFromDate(ds);
    if (dayIdx >= 0 && dayIdx < schedule.length) {
      totalScheduledDays++;
      const entry = history[ds];
      if (entry && entry.completedIds.length === entry.totalTasks) {
        completedDays++;
      }
    }
  }

  // Build grid cells
  const cells: React.ReactNode[] = [];
  // Empty cells before month starts
  for (let i = 0; i < startOffset; i++) {
    cells.push(<div key={`empty-${i}`} className="cal-cell cal-cell--outside" />);
  }
  // Day cells
  for (let d = 1; d <= daysInMonth; d++) {
    const ds = toDateStr(year, month, d);
    const dayIdx = getDayIndexFromDate(ds);
    const isSunday = dayIdx === -1;
    const isToday = ds === todayStr;
    const isPast = ds < todayStr;
    const entry = history[ds];
    const themeColor = !isSunday && dayIdx < schedule.length ? schedule[dayIdx].color : undefined;

    let dotClass = '';
    if (entry && entry.totalTasks > 0) {
      if (entry.completedIds.length === entry.totalTasks) dotClass = 'cal-dot--done';
      else if (entry.completedIds.length > 0) dotClass = 'cal-dot--partial';
      else if (isPast) dotClass = 'cal-dot--missed';
    } else if (isPast && !isSunday && themeColor) {
      dotClass = 'cal-dot--missed';
    }

    cells.push(
      <button
        key={d}
        className={`cal-cell ${isToday ? 'cal-cell--today' : ''} ${isSunday ? 'cal-cell--sunday' : ''}`}
        onClick={() => !isSunday && setSelectedDate(ds)}
        disabled={isSunday}
        style={themeColor ? { '--cell-color': themeColor } as React.CSSProperties : undefined}
      >
        <span className="cal-day-num">{d}</span>
        {themeColor && (
          <span className={`cal-dot ${dotClass}`} style={{ '--dot-bg': themeColor } as React.CSSProperties} />
        )}
      </button>
    );
  }

  // Selected date details
  const selectedEntry = selectedDate ? history[selectedDate] : null;
  const selectedDayIdx = selectedDate ? getDayIndexFromDate(selectedDate) : -1;
  const selectedSchedule = selectedDayIdx >= 0 && selectedDayIdx < schedule.length ? schedule[selectedDayIdx] : null;

  return (
    <div className="monthly-calendar">
      <PageHeader title="Calendar" />

      <div className="cal-content">
        {/* Month nav */}
        <div className="cal-nav">
          <button className="cal-nav-btn" onClick={goPrev}>‹</button>
          <h2 className="cal-month-title">{MONTH_NAMES[month]} {year}</h2>
          <button className="cal-nav-btn" onClick={goNext}>›</button>
        </div>

        {/* Month summary */}
        <div className="cal-summary">
          <span className="cal-summary-stat">{completedDays}</span>
          <span className="cal-summary-label"> of {totalScheduledDays} days completed</span>
        </div>

        {/* Day headers */}
        <div className="cal-grid cal-grid--headers">
          {DAY_HEADERS.map((h) => (
            <div key={h} className="cal-header-cell">{h}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="cal-grid">{cells}</div>

        {/* Legend */}
        <div className="cal-legend">
          <span className="cal-legend-item"><span className="cal-legend-dot cal-legend-dot--done" /> Complete</span>
          <span className="cal-legend-item"><span className="cal-legend-dot cal-legend-dot--partial" /> Partial</span>
          <span className="cal-legend-item"><span className="cal-legend-dot cal-legend-dot--missed" /> Missed</span>
        </div>
      </div>

      {/* Day detail sheet */}
      <BottomSheet
        open={!!selectedDate}
        onClose={() => setSelectedDate(null)}
        title={selectedSchedule ? `${selectedSchedule.day} — ${selectedSchedule.theme}` : selectedDate || ''}
      >
        {selectedSchedule ? (
          <div className="cal-detail">
            <div className="cal-detail-date">{selectedDate}</div>
            {selectedEntry ? (
              <div className="cal-detail-tasks">
                <p className="cal-detail-count">
                  {selectedEntry.completedIds.length} of {selectedEntry.totalTasks} tasks completed
                </p>
                {selectedSchedule.tasks.map((t) => (
                  <div
                    key={t.id}
                    className={`cal-detail-task ${selectedEntry.completedIds.includes(t.id) ? 'cal-detail-task--done' : ''}`}
                  >
                    <span className="cal-detail-check">
                      {selectedEntry.completedIds.includes(t.id) ? '✓' : '○'}
                    </span>
                    <span>{t.label}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="cal-detail-empty">No data recorded for this day.</p>
            )}
          </div>
        ) : (
          <p>No schedule for this day.</p>
        )}
      </BottomSheet>
    </div>
  );
};
